/**
 * Forgot Password Page Component (TASK-322)
 * Lets a user request a password reset link by email.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, Send } from 'lucide-react'
import apiClient from '@/services/api/client'

const GENERIC_SUCCESS_MESSAGE =
  'Если аккаунт с таким email существует, ссылка для сброса пароля отправлена'
const RATE_LIMIT_MESSAGE = 'Слишком много попыток. Попробуйте позже.'
const FALLBACK_ERROR_MESSAGE = 'Не удалось отправить запрос. Попробуйте ещё раз.'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [message, setMessage] = useState<string>(GENERIC_SUCCESS_MESSAGE)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const response = await apiClient.post('/api/v1/auth/password-reset/', {
        email,
      })
      setMessage(response.data?.message || GENERIC_SUCCESS_MESSAGE)
      setSubmitted(true)
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { status?: number; data?: { error?: string } }
      }
      if (axiosError.response?.status === 429) {
        setError(RATE_LIMIT_MESSAGE)
      } else {
        setError(axiosError.response?.data?.error || FALLBACK_ERROR_MESSAGE)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-blue-600">
            SMETA AI
          </h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
            Восстановление пароля
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Платформа автоматизации строительных смет
          </p>
        </div>

        {submitted ? (
          <div className="mt-8 space-y-6">
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
            <p className="text-center text-sm text-gray-600">
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Вернуться на страницу входа
              </Link>
            </p>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div>
              <Label htmlFor="email">Email адрес</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@mail.ru"
                className="mt-1"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Отправка...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Отправить
                </>
              )}
            </Button>

            <p className="text-center text-sm text-gray-600">
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Вернуться на страницу входа
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
